import { IngotIcon } from "@/ingot";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDecimal } from "@/lib/utils";

import {
  CUSTOM,
  MAX_QUANTITY,
  NO_VENDOR,
  cardOf,
  customMachineLabel,
  machineCards,
  newMachineEntry,
  variantSpecs,
  vendorOf,
  type MachineEntry,
  type OperationChoice,
  type UnverifiedMachine,
  type WizardMachine,
  type WizardOperation,
} from "./model";

/**
 * The machine's working space for this operation, or nothing when the catalog
 * does not state one. NOT ``width_m × depth_m × height_m``: that is the
 * machine's outer size, which a tenant read as what it makes (KAN-1343).
 */
function useEnvelope(): (machine: WizardMachine | undefined) => string {
  const { t } = useTranslation("admin");
  return (machine) => {
    const edges = machine?.build_volume_mm;
    if (!edges || edges.length === 0) return "";
    return t("onboardingStart.machine.buildVolume", {
      size: edges.map((edge) => Math.round(edge)).join(" × "),
    });
  };
}

/**
 * Výběr strojů operace: řádek na stroj a pod nimi „Přidat další stroj",
 * který otevře katalog seskupený podle výrobce (KAN-1197, podoba KAN-1318).
 *
 * Vlastník, 2026-09-11, nad plochou mřížkou 44 karet FDM: „musí tam být
 * výběr výrobce a pak výběr modelu a až pak případně doplňky nebo
 * varianty; musí být možnost přidat více strojů." Návrh „Rozjezd obchodu"
 * to drží jinou formou: modely stojí pod nadpisem svého výrobce a hledání
 * zúží obojí, takže model se pořád nehledá očima v celém katalogu.
 *
 * Varianta zůstává přepínačem u vybraného modelu, ne dalším modelem
 * v nabídce — vlastník, oddíl 7 brainstormu: „Varianta stroje je přepínač
 * na kartě, ne druhý řádek, který se musí najít." Doplňky jsou
 * přepínače pod ním a ukazují, co udělají — čísla počítá server.
 */
export function MachinePicker({
  op,
  choice,
  onPatch,
  allowCustom = true,
}: {
  op: WizardOperation;
  choice: OperationChoice;
  onPatch: (patch: Partial<OperationChoice>) => void;
  /**
   * „Můj stroj v katalogu není." Off for the anonymous pre-registration
   * (KAN-1473): an own machine needs the operation's config schema, which the
   * public catalog deliberately does not carry.
   */
  allowCustom?: boolean;
}): React.ReactElement {
  const { t, i18n } = useTranslation("admin");
  const envelope = useEnvelope();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [allUnverified, setAllUnverified] = useState(false);
  const cards = machineCards(op.machines);
  // Pořadí prvního výskytu v katalogu — to řadí platform admin, ne abeceda.
  const vendors = [...new Set(cards.map(vendorOf))];
  const needle = query.trim().toLowerCase();
  const listed = cards.filter((card) =>
    `${card.base.vendor ?? ""} ${card.base.name}`.toLowerCase().includes(needle),
  );
  const patchEntry = (id: number, patch: Partial<MachineEntry>) =>
    onPatch({
      machines: choice.machines.map((entry) =>
        entry.id === id ? { ...entry, ...patch } : entry,
      ),
    });
  const add = (entry: MachineEntry) => {
    onPatch({ machines: [...choice.machines, entry] });
    setOpen(false);
    setQuery("");
  };
  // Týž model bez doplňků zvedne počet (KAN-1319); řádek s doplňky je jiná
  // sestava, takže nový kus vedle něj vzniká jako samostatný řádek.
  const addCatalog = (machineKey: string) => {
    const same = choice.machines.find(
      (e) => e.machineKey === machineKey && e.addonKeys.length === 0,
    );
    if (!same) {
      add(newMachineEntry(op, machineKey));
      return;
    }
    patchEntry(same.id, {
      quantity: Math.min(MAX_QUANTITY, same.quantity + 1),
    });
    setOpen(false);
    setQuery("");
  };
  // KAN-1383 — an own operation has no catalog machine, so the platform's
  // machines are offered by identity only: the row is an own machine named
  // after the pick, with the operation's chosen materials and an empty config.
  const unverified = op.unverified_machines ?? [];
  const sameKind = unverified.filter((m) =>
    m.operation_categories.includes(op.operation_category),
  );
  const unverifiedListed = (allUnverified ? unverified : sameKind).filter((m) =>
    `${m.vendor ?? ""} ${m.name}`.toLowerCase().includes(needle),
  );
  const addUnverified = (machine: UnverifiedMachine) =>
    add({
      ...newMachineEntry(op),
      vendor: CUSTOM,
      machineKey: CUSTOM,
      customVendor: machine.vendor ?? "",
      customModel: machine.model ?? "",
      customName: machine.name,
      unverifiedKey: machine.machine_key,
    });
  const setQuantity = (id: number, next: number) =>
    patchEntry(id, {
      quantity: Math.max(1, Math.min(MAX_QUANTITY, Math.trunc(next) || 1)),
    });
  const addTestId = `onboarding-machine-add-${op.slug}`;

  return (
    <div className="stack" style={{ gap: 10 }}>
      {choice.machines.length === 0 && !open ? (
        <div className="zero">
          <IngotIcon name="archive" size={18} />
          <div>
            <b>{t("onboardingStart.machine.zeroTitle")}</b>
            <div className="t-small">{t("onboardingStart.machine.zeroText")}</div>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setOpen(true)}
            data-testid={addTestId}
          >
            <IngotIcon name="plus" /> {t("onboardingStart.machine.pick")}
          </button>
        </div>
      ) : null}

      {choice.machines.map((entry, index) => {
        const testId = (part: string) =>
          `onboarding-machine-${part}-${op.slug}-${index}`;
        const custom = entry.machineKey === CUSTOM;
        const selectedCard = cardOf(cards, entry.machineKey);
        const selected = selectedCard?.choices.find(
          (machine) => machine.machine_key === entry.machineKey,
        );
        const showVariants = (selectedCard?.choices.length ?? 0) > 1;
        // KAN-2019 — čím se varianty liší, u každé z nich (i u základu).
        const specs = showVariants && selectedCard
          ? variantSpecs(
              selectedCard.choices,
              op.machine_config_schema,
              (value) => formatDecimal(value, i18n.language),
              t("onboardingStart.machine.specMissing"),
            )
          : {};
        // Doplňky nabízené k VYBRANÉMU základu — jiný základ, jiná nabídka.
        const addons = (op.addons ?? []).filter((addon) =>
          selectedCard
            ? addon.applies_to_keys.includes(selectedCard.base.machine_key)
            : false,
        );
        return (
          <div
            key={entry.id}
            className="mrow"
            data-testid={testId("entry")}
            data-machine-key={entry.machineKey}
          >
            <div className="mrow-top">
              <div>
                <b>
                  {custom
                    ? customMachineLabel(entry) ||
                      t("onboardingStart.machine.custom")
                    : (selected?.name ?? "")}
                </b>
                <span
                  className="t-small"
                  style={{ display: "block" }}
                  data-testid={entry.unverifiedKey ? testId("unverified") : undefined}
                >
                  {entry.unverifiedKey
                    ? t("onboardingStart.machine.unverifiedWarning")
                    : custom
                      ? t("onboardingStart.machine.customDetail")
                      : envelope(selected)}
                </span>
              </div>
              <div className="qty">
                <span className="qty-lab t-small">
                  {t("onboardingStart.machine.qtyLabel")}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity(entry.id, entry.quantity - 1)}
                  disabled={entry.quantity <= 1}
                  aria-label={t("onboardingStart.machine.qtyLess")}
                  data-testid={testId("qty-less")}
                >
                  <IngotIcon name="minus" size={13} />
                </button>
                <input
                  className="mono"
                  inputMode="numeric"
                  value={entry.quantity}
                  aria-label={t("onboardingStart.machine.qtyInput")}
                  onChange={(e) =>
                    setQuantity(
                      entry.id,
                      parseInt(e.target.value.replace(/\D/g, ""), 10),
                    )
                  }
                  data-testid={testId("qty")}
                />
                <button
                  type="button"
                  onClick={() => setQuantity(entry.id, entry.quantity + 1)}
                  disabled={entry.quantity >= MAX_QUANTITY}
                  aria-label={t("onboardingStart.machine.qtyMore")}
                  data-testid={testId("qty-more")}
                >
                  <IngotIcon name="plus" size={13} />
                </button>
              </div>
              {/* KAN-1343: the last machine goes too — the step then shows
                  „Žádný stroj" and „Pokračovat" asks for one. Swapping a
                  printer used to mean adding a second one first. */}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  onPatch({
                    machines: choice.machines.filter(
                      (other) => other.id !== entry.id,
                    ),
                  })
                }
                data-testid={testId("remove")}
              >
                <IngotIcon name="trash" />{" "}
                {t("onboardingStart.machine.remove")}
              </button>
            </div>
            {/* Always rendered, hidden at one piece: inserting it on „+"
                pushed the materials grid down under the cursor and the next
                click landed on another card (KAN-1343). */}
            <div
              className="qtynote t-small"
              data-testid={testId("qty-note")}
              aria-hidden={entry.quantity <= 1}
              style={{ visibility: entry.quantity > 1 ? "visible" : "hidden" }}
            >
              {t(
                addons.length > 0
                  ? "onboardingStart.machine.qtyNoteAddons"
                  : "onboardingStart.machine.qtyNote",
                {
                  machines: t("onboardingStart.tags.machines", {
                    count: entry.quantity,
                  }),
                },
              )}
            </div>

            {showVariants || addons.length > 0 ? (
              <div className="addons" data-testid={testId("variants")}>
                {showVariants && selectedCard ? (
                  <div className="variants">
                    <span className="t-small">
                      {t("onboardingStart.machine.variant")}
                    </span>
                    <div
                      className="chips"
                      role="group"
                      aria-label={t("onboardingStart.machine.variant")}
                    >
                      {selectedCard.choices.map((variant) => (
                        <button
                          key={variant.machine_key}
                          type="button"
                          className="chip"
                          aria-pressed={variant.machine_key === entry.machineKey}
                          onClick={() =>
                            patchEntry(entry.id, {
                              machineKey: variant.machine_key,
                            })
                          }
                          data-testid={`${testId("variant")}-${variant.machine_key}`}
                        >
                          {variant.name}
                          {specs[variant.machine_key] ? (
                            <span
                              className="t-small"
                              style={{ display: "block" }}
                              data-testid={`${testId("variant-spec")}-${variant.machine_key}`}
                            >
                              {specs[variant.machine_key]}
                            </span>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
                {addons.map((addon) => {
                  const on = entry.addonKeys.includes(addon.machine_key);
                  // Co doplněk udělá, počítá server — karta to jen vypisuje.
                  const changes = Object.entries(addon.changes)
                    .map(
                      ([key, [before, after]]) =>
                        `${key}: ${before === null || before === undefined ? "—" : String(before)} → ${String(after)}`,
                    )
                    .join(" · ");
                  return (
                    <label key={addon.machine_key} className="addon">
                      <span className="switch">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() =>
                            patchEntry(entry.id, {
                              addonKeys: on
                                ? entry.addonKeys.filter(
                                    (k) => k !== addon.machine_key,
                                  )
                                : [...entry.addonKeys, addon.machine_key],
                            })
                          }
                          data-testid={`${testId("addon")}-${addon.machine_key}`}
                        />
                        <span />
                      </span>
                      <span>
                        <b>{addon.name}</b>
                        {addon.description ? (
                          <span className="t-small" style={{ display: "block" }}>
                            {addon.description}
                          </span>
                        ) : null}
                        {on && changes ? (
                          <span
                            className="t-small"
                            style={{ display: "block", color: "var(--ink-4)" }}
                          >
                            {changes}
                            {entry.quantity > 1
                              ? ` · ${t("onboardingStart.machine.addonAll", { count: entry.quantity })}`
                              : ""}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : null}

            {custom ? (
              <div className="mrow-body">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 12,
                  }}
                >
                  <label className="field">
                    <span className="label">
                      {t("onboardingStart.machine.vendorLabel")}
                    </span>
                    <input
                      type="text"
                      className="input"
                      maxLength={128}
                      value={entry.customVendor}
                      onChange={(e) =>
                        patchEntry(entry.id, {
                          customVendor: e.target.value,
                          customName: undefined,
                        })
                      }
                      data-testid={testId("vendor")}
                    />
                  </label>
                  <label className="field">
                    <span className="label">
                      {t("onboardingStart.machine.modelLabel")}
                    </span>
                    <input
                      type="text"
                      className="input"
                      maxLength={128}
                      value={entry.customModel}
                      onChange={(e) =>
                        patchEntry(entry.id, {
                          customModel: e.target.value,
                          customName: undefined,
                        })
                      }
                      data-testid={testId("model")}
                    />
                  </label>
                </div>
                {op.materials.length > 0 && !entry.unverifiedKey ? (
                  <div className="field">
                    <span className="label">
                      {t("onboardingStart.machine.materialsLabel")}
                    </span>
                    <div
                      className="chips"
                      role="group"
                      aria-label={t("onboardingStart.machine.materialsLabel")}
                    >
                      {op.materials.map((material) => (
                        <button
                          key={material.code}
                          type="button"
                          className="chip"
                          aria-pressed={entry.customMaterialCodes.includes(
                            material.code,
                          )}
                          onClick={() =>
                            patchEntry(entry.id, {
                              customMaterialCodes:
                                entry.customMaterialCodes.includes(material.code)
                                  ? entry.customMaterialCodes.filter(
                                      (code) => code !== material.code,
                                    )
                                  : [...entry.customMaterialCodes, material.code],
                            })
                          }
                          data-testid={`${testId("material")}-${material.code}`}
                        >
                          {material.display_name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}

      {!open && choice.machines.length > 0 ? (
        <button
          type="button"
          className="addrow"
          onClick={() => setOpen(true)}
          data-testid={addTestId}
        >
          <IngotIcon name="plus" size={15} /> {t("onboardingStart.machine.add")}
        </button>
      ) : null}

      {open ? (
        <div className="picker" data-testid={`onboarding-machine-picker-${op.slug}`}>
          <div className="picker-head">
            <div className="search" style={{ flex: 1 }}>
              <IngotIcon name="search" />
              <input
                className="input"
                placeholder={t("onboardingStart.machine.search")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid={`onboarding-machine-search-${op.slug}`}
              />
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setOpen(false);
                setQuery("");
              }}
            >
              <IngotIcon name="close" /> {t("onboardingStart.machine.close")}
            </button>
          </div>

          <div className="picker-list">
            {vendors.map((vendor) => {
              const rows = listed.filter((card) => vendorOf(card) === vendor);
              if (rows.length === 0) return null;
              return (
                <div key={vendor}>
                  <div className="picker-group">
                    {vendor === NO_VENDOR
                      ? t("onboardingStart.machine.otherVendor")
                      : vendor}
                  </div>
                  {rows.map((card) => {
                    const have = choice.machines
                      .filter((e) => cardOf(cards, e.machineKey) === card)
                      .reduce((n, e) => n + e.quantity, 0);
                    return (
                      <button
                        key={card.base.machine_key}
                        type="button"
                        className="picker-row"
                        onClick={() => addCatalog(card.base.machine_key)}
                        data-testid={`onboarding-machine-pick-${op.slug}-${card.base.machine_key}`}
                      >
                        <span>
                          <b>{card.base.name}</b>
                          <span className="t-small" style={{ display: "block" }}>
                            {[
                              envelope(card.base),
                              t("onboardingStart.machine.materialsCount", {
                                count: op.materials.filter((m) =>
                                  card.base.material_codes?.includes(m.code),
                                ).length,
                              }),
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </span>
                        {have > 0 ? (
                          <span className="tag tag-ok">
                            {t("onboardingStart.machine.have", { count: have })}
                          </span>
                        ) : null}
                        <span className="picker-cta">
                          <IngotIcon name="plus" size={13} />{" "}
                          {have > 0
                            ? t("onboardingStart.machine.addMore")
                            : t("onboardingStart.machine.addOne")}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
            {unverified.length > 0 ? (
              <div data-testid={`onboarding-machine-unverified-${op.slug}`}>
                <div className="t-small" style={{ padding: "10px 14px 4px" }}>
                  {t("onboardingStart.machine.unverifiedWarning")}
                </div>
                {unverifiedListed.map((machine) => (
                  <button
                    key={machine.machine_key}
                    type="button"
                    className="picker-row"
                    onClick={() => addUnverified(machine)}
                    data-testid={`onboarding-machine-unverified-pick-${op.slug}-${machine.machine_key}`}
                  >
                    <span>
                      <b>{machine.name}</b>
                      {machine.vendor ? (
                        <span className="t-small" style={{ display: "block" }}>
                          {machine.vendor}
                        </span>
                      ) : null}
                    </span>
                    <span className="picker-cta">
                      <IngotIcon name="plus" size={13} />{" "}
                      {t("onboardingStart.machine.addOne")}
                    </span>
                  </button>
                ))}
                {!allUnverified && sameKind.length < unverified.length ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ margin: "6px 14px" }}
                    onClick={() => setAllUnverified(true)}
                    data-testid={`onboarding-machine-unverified-all-${op.slug}`}
                  >
                    {t("onboardingStart.machine.unverifiedAll")}
                  </button>
                ) : null}
              </div>
            ) : null}
            {listed.length === 0 && unverifiedListed.length === 0 ? (
              <div className="t-small" style={{ padding: "14px 14px 4px" }}>
                {t("onboardingStart.machine.notFound")}
              </div>
            ) : null}
          </div>

          {allowCustom ? (
            <div className="picker-foot">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() =>
                  add({ ...newMachineEntry(op), vendor: CUSTOM, machineKey: CUSTOM })
                }
                data-testid={`onboarding-machine-custom-${op.slug}`}
              >
                <IngotIcon name="plus" /> {t("onboardingStart.machine.customOpen")}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
